import { useEffect, useRef } from 'react';

// Every branch excludes tabindex="-1", not just the bare [tabindex] one: a
// roving-tabindex group (radio chips, menu items) leaves most of its buttons
// untabbable, and treating one of those as the first/last stop let Shift+Tab
// leak out of the dialog.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]',
].map((sel) => `${sel}:not([tabindex="-1"])`).join(', ');

// Minimum viable accessible-dialog behavior for a modal whose root element
// holds `ref` and `tabIndex={-1}`: focuses the panel on open so screen
// readers announce it immediately, closes on Escape, keeps Tab cycling
// inside the panel instead of leaking focus to the page underneath, and
// restores focus to whatever triggered the modal once it closes.
//
// `isOpen` defaults to true for modal components that only ever mount while
// open (the parent renders them behind `{condition && <Modal />}`). Pass it
// explicitly for a modal that stays mounted and toggles its own visibility
// (e.g. an `open` prop) — otherwise this effect only runs once, on that
// component's initial mount, and never re-arms on a later open.
export default function useModalA11y(ref, onClose, isOpen = true) {
  const previouslyFocused = useRef(null);
  // Callers overwhelmingly pass an inline `() => {...}` for onClose, a fresh
  // function identity every render. Read the latest one through this ref
  // instead of closing over the prop directly, so the setup/teardown effect
  // below only depends on `ref`/`isOpen` — not on onClose's churn. Without
  // this, the effect re-ran on every keystroke in any field inside the
  // modal: its cleanup restored focus to whatever triggered the modal, then
  // the effect's own re-run immediately moved focus again (to `node`, since
  // the just-restored element sat outside it) — visibly, typing one
  // character kicked focus out of the field.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    previouslyFocused.current = document.activeElement;
    // Don't steal focus from a field the panel already autoFocuses — just
    // make sure focus starts *somewhere* inside so Escape/Tab below reach it.
    if (!node.contains(document.activeElement)) node.focus();

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        if (onCloseRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = node.querySelectorAll(FOCUSABLE_SELECTOR);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const toRestore = previouslyFocused.current;
      if (toRestore && typeof toRestore.focus === 'function' && document.contains(toRestore)) {
        toRestore.focus({ preventScroll: true });
      }
    };
  }, [ref, isOpen]);
}
